// Reserved project labels describing where a project sits in the organization, e.g. Hospital > Building

export const LABEL_KEY_HOSPITAL = 'dt-hospital' as const;
export const LABEL_KEY_BUILDING = 'dt-building' as const;

export interface OrgStructureLevel {
    key: string;
    displayName: string;
}

// Ordered top-down
export const ORG_STRUCTURE_LEVELS: readonly OrgStructureLevel[] = [
    { key: LABEL_KEY_HOSPITAL, displayName: 'Hospital' },
    { key: LABEL_KEY_BUILDING, displayName: 'Building' },
];

type Labels = Record<string, string> | undefined;

// One value per drilled level; null selects projects without that label
export type OrgStructurePath = (string | null)[];

export interface OrgStructureNode<T> {
    value: string | null;
    items: T[];
}

export function getOrgStructureValue(labels: Labels, key: string): string | null {
    return (labels && labels[key]) || null;
}

export function isInOrgStructurePath(labels: Labels, path: OrgStructurePath): boolean {
    return path.every((value, index) => getOrgStructureValue(labels, ORG_STRUCTURE_LEVELS[index].key) === value);
}

export function hasOrgStructureValue(labels: Labels): boolean {
    return ORG_STRUCTURE_LEVELS.some(level => getOrgStructureValue(labels, level.key) !== null);
}

// Sorted by value, with the "not set" node last
export function groupByOrgStructureLevel<T>(items: T[], getLabels: (item: T) => Labels, level: OrgStructureLevel): OrgStructureNode<T>[] {
    const groups = new Map<string | null, T[]>();
    items.forEach(item => {
        const value = getOrgStructureValue(getLabels(item), level.key);
        groups.set(value, [...(groups.get(value) || []), item]);
    });
    return Array.from(groups.entries())
        .map(([value, groupItems]) => ({ value, items: groupItems }))
        .sort((a, b) => {
            if (a.value === null) return 1;
            if (b.value === null) return -1;
            return a.value.localeCompare(b.value, undefined, { numeric: true });
        });
}
